import React from "react";
import { motion } from "framer-motion";
import { TrendingUp } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { formatCurrency, formatCurrencyShort, tooltipStyle } from "./utils";

interface RevenueTrendChartProps {
  data: { month: string; billedCents: number; collectedCents: number }[];
}

const RevenueTrendChart: React.FC<RevenueTrendChartProps> = ({ data }) => {
  const chartData = data.map((d) => ({
    month: d.month,
    billed: d.billedCents / 100,
    collected: d.collectedCents / 100,
  }));

  const isEmpty = !data.length || data.every((d) => d.billedCents === 0 && d.collectedCents === 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <div className="p-2 rounded-lg" style={{ backgroundColor: "#0F51321A" }}>
          <TrendingUp className="h-4 w-4" style={{ color: "#0F5132" }} />
        </div>
        <h3 className="text-sm font-semibold text-slate-700 m-0 leading-none">Revenue Trend</h3>
      </div>

      {isEmpty ? (
        <div className="flex flex-col items-center justify-center h-64 text-center px-6">
          {/* Decorative mini chart illustration */}
          <div className="relative mb-5">
            <div className="h-20 w-44 flex items-end justify-center gap-1.5">
              {[28, 42, 35, 55, 48, 62, 50].map((h, i) => (
                <motion.div
                  key={i}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: `${h}%`, opacity: 1 }}
                  transition={{ delay: i * 0.08, duration: 0.5, ease: "easeOut" }}
                  className="w-4 rounded-t-md"
                  style={{ backgroundColor: i % 2 === 0 ? "#0F513220" : "#19875420" }}
                />
              ))}
            </div>
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="absolute bottom-0 left-0 right-0 h-px bg-slate-200 origin-left"
            />
          </div>
          <p className="text-sm font-semibold text-slate-600 mb-1">No revenue data yet</p>
          <p className="text-xs text-slate-400 max-w-[240px]">
            Revenue trends will appear here once you start billing clients and recording payments
          </p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 12, fill: "#64748b" }}
            />
            <YAxis
              tick={{ fontSize: 12, fill: "#64748b" }}
              tickFormatter={(v) => formatCurrencyShort(v * 100)}
            />
            <Tooltip
              formatter={(value: number | string | undefined, name: string | undefined) => [
                formatCurrency(Number(value ?? 0) * 100),
                name === "billed" ? "Billed" : "Collected",
              ]}
              contentStyle={tooltipStyle}
            />
            <Legend
              formatter={(value: string) => (
                <span className="text-xs text-slate-600 capitalize">
                  {value}
                </span>
              )}
            />
            <Line
              type="monotone"
              dataKey="billed"
              stroke="#0F5132"
              strokeWidth={2}
              dot={{ fill: "#0F5132", r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="collected"
              stroke="#198754"
              strokeWidth={2}
              dot={{ fill: "#198754", r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </motion.div>
  );
};

export default RevenueTrendChart;
